import React, { useState, useEffect } from 'react'
import * as EmailValidator from 'email-validator'
import { notification, Modal, message } from 'antd'
import { motion } from 'framer-motion' // not "framer-motion/dist/framer-motion"
import ReCAPTCHA from 'react-google-recaptcha'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'

import api from '../../services/api'
import { setToken, setUser } from '../../guard'
import { Card, Col, Grid, Page } from './styles'

import Steps from '../../components/Steps'
import Input from '../../components/Input'
import Button from '../../components/Button'

import logoSrc from '../../assets/images/logo-white.png'
import { Helmet } from 'react-helmet'

// Animação de rotação para o spinner
const spinAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid var(--primary);
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spinAnimation} 1s linear infinite;
`;

function Register() {
  const navigate = useNavigate()
  const location = useLocation()
  const [visible, setVisible] = useState(false)

  const [loading, setLoading] = useState(false)
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [discord, setDiscord] = useState('')
  const [response, setResponse] = useState('')
  const [repeatPassword, setRepeatPassword] = useState('')

  const transition = {
    duration: 0.5,
    ease: [0.43, 0.13, 0.23, 0.96],
  }

  const backVariants = {
    exit: { x: 100, opacity: 0, transition },
    enter: { x: 0, opacity: 1, transition: { delay: 0, ...transition } },
  }

  const [step, setStep] = useState(0)
  const [completed, setCompleted] = useState(false)
  const [readTerms, setReadTerms] = useState(false)
  const [recaptchaLoading, setRecaptchaLoading] = useState(true)

  // Handle input changes
  const handleUsernameChange = (e) => {
    if (e && e.target) {
      setUsername(e.target.value);
    }
  };

  const handleEmailChange = (e) => {
    if (e && e.target) {
      setEmail(e.target.value);
    }
  };

  const handlePasswordChange = (e) => {
    if (e && e.target) {
      setPassword(e.target.value);
    }
  };

  const handleRepeatPasswordChange = (e) => {
    if (e && e.target) {
      setRepeatPassword(e.target.value);
    }
  };

  // Use Effect para recuperar dados do localStorage e location state
  useEffect(() => {
    // Recuperar dados do localStorage caso venha do callback do Discord
    const savedData = localStorage.getItem('register_data');
    if (savedData) {
      const data = JSON.parse(savedData);
      if (data.username) setUsername(data.username);
      if (data.email) setEmail(data.email);
      if (data.password) setPassword(data.password);
      if (data.repeatPassword) setRepeatPassword(data.repeatPassword);
      if (data.discord) setDiscord(data.discord);
    }

    // Verificar se veio do callback do Discord
    if (location.state && location.state.step !== undefined) {
      setStep(location.state.step);
      if (location.state.discordData && location.state.discordData.discord) {
        // Formatar o nome de usuário do Discord antes de defini-lo
        const formattedDiscord = formatDiscordUsername(location.state.discordData.discord);
        setDiscord(formattedDiscord);
      }
    }
  }, [location]);

  function isValidDiscordUsername(inputStr) {
    // O Discord agora usa apenas o nome do usuário sem discriminador, mas podemos manter 
    // compatibilidade com o formato antigo também (username#0000)
    const oldPattern = /^\w{2,32}#\d{4}$/
    const newPattern = /^[a-zA-Z0-9_]{2,32}$/ // Novo formato do Discord
    
    // Consideramos válido se for qualquer um dos dois formatos
    return newPattern.test(inputStr) || oldPattern.test(inputStr)
  }

  // Função para limpar discriminador #0 se presente
  const formatDiscordUsername = (username) => {
    if (!username) return '';
    
    // Se for "NÃO VINCULADO", retorna como está
    if (username === "NÃO VINCULADO") return username;
    
    // Remove o '#0' do final do nome de usuário
    if (username.endsWith('#0')) {
      return username.substring(0, username.length - 2);
    }
    
    return username;
  };

  async function submit() {
    switch (step) {
      case 0:
        if (!username || !email || !password || !repeatPassword) {
          return notification.warning({
            message: 'Revise seus dados',
            description: 'Informe todos os campos necessários.',
            placement: 'bottomRight',
          })
        }

        if (!EmailValidator.validate(email)) {
          return notification.warning({
            message: 'Revise seus dados',
            description: 'Endereço de e-mail inválido, tente outro.',
            placement: 'bottomRight',
          })
        }

        if (!readTerms) {
          return notification.warning({
            message: 'Revise seus dados',
            description: 'Aceite os termos & condições para continuar',
            placement: 'bottomRight',
          })
        }

        if (password.length > 30 || password.length < 6) {
          return notification.warning({
            message: 'Revise seus dados',
            description: 'Sua senha deve ter entre 6 a 30 caracteres',
            placement: 'bottomRight',
          })
        }

        if (password !== repeatPassword) {
          return notification.warning({
            message: 'Opa, calma ai!',
            description: 'Suas senhas não são iguais',
            placement: 'bottomRight',
          })
        }

        setLoading(true)

        try {
          await api.post('/user/register/step/1', {
            username,
            email,
          })

          setStep(step + 1)
        } catch (err) {
          notification.warning({
            message: 'Opa, calma ai!',
            description: err.response.data.message,
            placement: 'bottomRight',
          })
        }
        setLoading(false)
        break

      case 1:
        if (discord.length <= 0) {
          return notification.warning({
            message: 'Opa, calma ai!',
            description: 'Por favor, vincule sua conta do Discord ou clique em "Não vincular"',
            placement: 'bottomRight',
          })
        }

        // Verifica se o usuário optou por não vincular
        if (discord !== "NÃO VINCULADO") {
          // Só valida o formato se não for "NÃO VINCULADO"
          if (!isValidDiscordUsername(discord)) {
            return notification.warning({
              message: 'Opa, username do Discord inválido!',
              description: 'Por favor, verifique seu username do Discord se está correto.',
              placement: 'bottomRight',
            })
          }
        }

        setLoading(true)

        try {
          // Usar a versão formatada do discord ao enviar para a API
          const formattedDiscord = formatDiscordUsername(discord);
          
          await api.post('/user/register/step/2', {
            discord: formattedDiscord,
          })

          setStep(step + 1)
        } catch (err) {
          notification.warning({
            message: 'Opa, calma ai!',
            description: err.response.data.message,
            placement: 'bottomRight',
          })
        }
        setLoading(false)

        break
      case 2:
        setStep(step + 1)
        break

      default:
        return notification.error({
          message: 'Ocorreu um erro',
          description: 'Tente atualizar sua página, ocorreu um erro :(',
          placement: 'bottomRight',
        })
    }
  }

  async function completeForm() {
    if (!username || !email || !password || !repeatPassword) {
      return notification.warning({
        message: 'Revise seus dados',
        description: 'Informe todos os campos necessários.',
        placement: 'bottomRight',
      })
    }

    if (!response) {
      return notification.warning({
        message: 'Revise seus dados',
        description: 'Você precisa completar a verificação reCAPTCHA para continuar.',
        placement: 'bottomRight',
      })
    }

    setLoading(true)

    try {
      // Usar a versão formatada do discord ao enviar para a API
      const formattedDiscord = formatDiscordUsername(discord);
      
      const result = await api.post('/user', {
        username,
        email,
        password,
        discord: formattedDiscord,
        response,
      })
      const { user, token } = result.data

      // Limpar dados do localStorage após cadastro com sucesso
      localStorage.removeItem('register_data');

      setUser(user)
      setToken(token)

      navigate('/dashboard')

      message.success(`Bem-vindo, ${username}`)
    } catch (err) {
      console.error('Erro ao completar cadastro:', err);
      
      // Limpa o reCAPTCHA e reseta o estado para garantir que o usuário possa tentar novamente
      setCompleted(false);
      setResponse('');
      
      if (window.grecaptcha) {
        try {
          window.grecaptcha.reset();
        } catch (resetErr) {
          console.error('Erro ao resetar reCAPTCHA:', resetErr);
        }
      }
      
      const errorMessage = err.response?.data?.message || 'Ocorreu um erro ao concluir o cadastro. Por favor, tente novamente.';
      
      return notification.error({
        message: 'Oops!, ocorreu um erro',
        description: errorMessage,
        placement: 'bottomRight',
      })
    } finally {
      setLoading(false);
    }
  }

  // Função para não vincular o Discord
  const handleSkipDiscord = () => {
    setDiscord("NÃO VINCULADO");
    notification.warning({
      message: 'Atenção',
      description: 'Você optou por não vincular uma conta do Discord. Isso pode limitar sua experiência e suporte.',
      placement: 'bottomRight',
    });
  };

  // Adicionando função para autenticação com Discord
  const handleDiscordAuth = () => {
    const DISCORD_CLIENT_ID = '1349808940883382323'; // Substitua pelo seu Client ID
    const REDIRECT_URI = encodeURIComponent('http://localhost:3000/auth/discord/callback');
    const DISCORD_AUTH_URL = `https://discord.com/api/oauth2/authorize?client_id=${DISCORD_CLIENT_ID}&redirect_uri=${REDIRECT_URI}&response_type=code&scope=identify%20email`;
    
    // Salvando o estado atual do cadastro no localStorage para recuperar depois do OAuth
    localStorage.setItem('register_data', JSON.stringify({
      username,
      email,
      password,
      repeatPassword
    }));
    
    // Redirecionando para autenticação do Discord
    window.location.href = DISCORD_AUTH_URL;
  };

  return (
    <>
      <Helmet>
        <title>Criar uma conta - Atlas Plugins</title>
      </Helmet>
      <Page>
        <Grid>
          <Col>
            <Card layoutId="card">
              <Steps
                rows={[
                  { step: '1', title: 'Dados pessoais' },
                  { step: '2', title: 'Discord' },
                  { step: '3', title: 'Recaptcha' },
                ]}
                activeIndex={step}
              />
              {step === 0 && (
                <>
                  <Input
                    inputProps={{
                      placeholder: 'Informe seu nome',
                    }}
                    value={username}
                    onChange={handleUsernameChange}
                  />
                  <Input
                    inputProps={{
                      placeholder: 'Informe seu e-mail',
                    }}
                    value={email}
                    onChange={handleEmailChange}
                  />
                  <Input
                    inputProps={{
                      placeholder: 'Informe uma senha',
                    }}
                    isPassword
                    value={password}
                    onChange={handlePasswordChange}
                  />
                  <Input
                    inputProps={{
                      placeholder: 'Repita sua senha',
                    }}
                    isPassword
                    value={repeatPassword}
                    onChange={handleRepeatPasswordChange}
                  />
                  <label className="terms-and-conditions">
                    <input
                      type="checkbox"
                      onClick={() => {
                        setReadTerms(true)
                      }}
                      required
                    />{' '}
                    Eu declaro que li e aceito todos os{' '}
                    <a href="#terms-and-services" onClick={setVisible}>
                      termos & condições da Atlas Plugins
                    </a>{' '}
                    e não descumprirei nenhuma regra.
                  </label>
                </>
              )}
              {step === 1 && (
                <>
                  <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <p>Vincule sua conta do Discord para continuar</p>
                    <Button 
                      backgroundColor="#7289DA" // Cor do Discord
                      color="#fff"
                      loading={loading}
                      onClick={handleDiscordAuth}
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        gap: '10px', 
                        margin: '0 auto', 
                        marginBottom: '15px',
                        height: '42px',
                        padding: '0 20px'
                      }}
                    >
                      <img 
                        src="https://cdn.prod.website-files.com/6257adef93867e50d84d30e2/66e3d7f4ef6498ac018f2c55_Symbol.svg"
                        alt="Discord Logo"
                        style={{ 
                          width: '20px', 
                          height: '20px', 
                          marginTop: '-1px',
                          objectFit: 'contain',
                          display: 'inline-block',
                          verticalAlign: 'middle'
                        }}
                      />
                      <span style={{ display: 'inline-block', verticalAlign: 'middle' }}>Entrar com Discord</span>
                    </Button>
                    
                    <Button 
                      backgroundColor="#f5f5f5" 
                      color="#666"
                      onClick={handleSkipDiscord}
                      style={{ margin: '0 auto' }}
                    >
                      Não vincular (não recomendado)
                    </Button>
                    
                    {discord && (
                      <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                        <p>
                          <strong>Status:</strong> {discord === "NÃO VINCULADO" ? 
                            <span style={{ color: '#ff4d4f' }}>Sem vinculação</span> : 
                            <span style={{ color: '#52c41a' }}>Vinculado como {formatDiscordUsername(discord)}</span>
                          }
                        </p>
                      </div>
                    )}
                  </div>
                </>
              )}
              {step === 2 && (
                <>
                  <div className="recaptcha-container" style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    marginBottom: '20px'
                  }}>
                    {recaptchaLoading && (
                      <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                        <LoadingSpinner />
                        <p>Carregando captcha...</p>
                      </div>
                    )}
                    <ReCAPTCHA
                      sitekey="6LfWxhkqAAAAAIIu5yzv4BLrIcDBCL-hXQoxlKh4"
                      onChange={(value) => {
                        console.log("reCAPTCHA value:", value);
                        setResponse(value);
                        setCompleted(true);
                      }}
                      onExpired={() => {
                        console.log("reCAPTCHA expired");
                        setResponse("");
                        setCompleted(false);
                      }}
                      onErrored={() => {
                        console.log("reCAPTCHA error");
                        setRecaptchaLoading(false);
                      }}
                      hl="pt-BR"
                      theme="light"
                      ref={(el) => {
                        if (el) {
                          console.log("reCAPTCHA ref loaded");
                          setRecaptchaLoading(false);
                        }
                      }}
                    />
                    {!completed && !recaptchaLoading && (
                      <div style={{ 
                        marginTop: '10px', 
                        padding: '8px', 
                        backgroundColor: '#fff8f8', 
                        border: '1px solid #ffccc7',
                        borderRadius: '4px',
                        color: '#f5222d',
                        textAlign: 'center'
                      }}>
                        Marque a caixa acima para verificar que você não é um robô
                      </div>
                    )}
                    {completed && (
                      <div style={{ 
                        marginTop: '10px', 
                        padding: '8px', 
                        backgroundColor: '#f6ffed', 
                        border: '1px solid #b7eb8f',
                        borderRadius: '4px',
                        color: '#52c41a',
                        textAlign: 'center'
                      }}>
                        Verificação concluída com sucesso!
                      </div>
                    )}
                  </div>
                </>
              )}
              {step === 2 && completed && (
                <Button
                  backgroundColor="#000"
                  margin="25px 0px 10px 0px"
                  color="#fff"
                  loading={loading}
                  onClick={completeForm}
                >
                  Completar cadastro
                </Button>
              )}
              {step < 2 && (
                <Button backgroundColor="var(--primary)" color="#fff" loading={loading} onClick={submit}>
                  {step < 2 ? 'Prosseguir' : 'Concluir cadastro'}
                </Button>
              )}
              {step > 0 && step <= 2 && (
                <Button
                  backgroundColor="transparent"
                  color="#000"
                  margin={step === 2 && !completed ? '25px 0px 10px 0px' : '0px'}
                  onClick={() => {
                    setStep(step - 1)
                    setCompleted(false)
                  }}
                >
                  Voltar
                </Button>
              )}
            </Card>
          </Col>
          <Col right>
            <motion.div variants={backVariants}>
              <motion.img layoutId="atlasLogo" src={logoSrc} alt="Atlas Loja - Login" />
              <motion.h1 layoutId="atlasHeading">
                Faça seu servidor <br /> <b>voar nas núvens</b> <br /> sem obstáculos!
              </motion.h1>
              <p>
                Cadastre-se e faça parte da maior loja Brasileira de plugins <br /> de minecraft e faça seu servidor
                voar nas núvens.
              </p>
              <Link to="/login">Voltar para o login</Link>
            </motion.div>
          </Col>
        </Grid>
      </Page>
      <Modal
        title="Políticas de compra"
        visible={visible}
        cancelText="Fechar"
        okText="Concordo"
        onCancel={() => setVisible(false)}
        onOk={() => setVisible(false)}
      >
        <h4 className="font-weight-bold">Atenção!</h4>
        <p>
          É proibida a utilização de qualquer modificação que tragam qualquer tipo de vantagem para quem os utiliza;{' '}
          <br />
          É proibido qualquer divulgação de qualquer plugin/serviço prestado por nós; <br />
          É proibido editar ou tentar decompilar nossos projetos, caso aconteça isso e seja descoberto corre risco de
          remoção da licença; <br />É EXTREMAMENTE PROIBIDO a divulgação de qualquer arquivo de nosso site para outras
          pessoas para serem divulgadas ou editadas ou decompiladas, correndo o risco do cancelamento total da licença
          do usuário sem reembolso.
        </p>
        <h4 className="font-weight-bold">Política de Reembolso</h4>
        <p>
          Após a finalização da compra em nossa loja, nos reservamos no direito de fornecer reembolso apenas em ocasiões
          em que nosso serviço pare de funcionar, caso contrário todo suporte possível que a equipe ATLAS PLUGINS será
          fornecido. <br />
          A entrega do produto é automática e sua licença é liberada assim que o pagamento é aprovado. Em caso de
          suspensão da conta do usuário, o reembolso não será feito, mesmo que tal suspensão impeça o usuário de
          usufruir dos benefícios adquridos. <br />
          Ao adquirir um produto em nossa loja, você concorda que está adquirindo produtos 100% virtuais e não passíveis
          de devolução.
        </p>
        <h4 className="font-weight-bold">Política de Privacidade</h4>
        <p>
          O grupo Atlas Plugins se firma no dever de manter todas as informações pessoais do usuário em sigílo total.
          Nunca distribuiremos ou venderemos informações existentes em nossos cadastros.
          <br />
          <br />O grupo de Discord da Atlas Plugins não libera a venda de nossos plugins pelo nosso discord (Os plugins
          comprados na loja podem ser vendidos por fora de nosso discord.)
        </p>
      </Modal>
    </>
  )
}

export default Register
